package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** U+2026, one glyph. Three periods would be three characters of the identifier's budget. */
private const val ELLIPSIS = "…"

/** A UUID's first two hyphenated groups — the head that names the run. */
private const val ELIDE_HEAD_CHARS = 13

/** A UUID's last group — the tail a person matches against a support ticket. */
private const val ELIDE_TAIL_CHARS = 8

/**
 * Head and tail of [value], joined by an ellipsis, computed rather than clipped.
 *
 * A UUID differs from its neighbours at both ends — `9f3c1e7a-…` names the run and `…7a6f41b8` is
 * what a person matches against a support ticket — so a tail-truncated id is an id you cannot check.
 * CSS `text-overflow` can only drop the tail, which is why this is arithmetic and not a style rule.
 *
 * The defaults reproduce the receipt spec's rendered form exactly: a 36-character UUID becomes
 * `9f3c1e7a-4b62` + `…` + `7a6f41b8`. They are group boundaries, not a symmetric split — see
 * [ELIDE_HEAD_CHARS] and [ELIDE_TAIL_CHARS].
 *
 * Values short enough to survive whole are returned untouched, including the boundary case where
 * eliding would cost a character and save none.
 */
fun elideMiddle(
    value: String,
    headChars: Int = ELIDE_HEAD_CHARS,
    tailChars: Int = ELIDE_TAIL_CHARS,
): String {
    val head = headChars.coerceAtLeast(0)
    val tail = tailChars.coerceAtLeast(0)
    if (value.length <= head + tail + ELLIPSIS.length) return value
    return value.take(head) + ELLIPSIS + value.takeLast(tail)
}

val LCopyFieldStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "flex") }
        .alignItems(AlignItems.Center)
        .width(320.px)
        .height(ControlHeight.MD)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        // `borderStrong`, and the brief (`docs/design/run-receipt.md` §2.1) names this token — an
        // earlier draft stroked the field with `border` and that was logged as a rule 10 BLOCKER.
        // This field is input-shaped and holds a button, so its edge is a control boundary with a
        // 3:1 floor, and the hairline has two grounds to clear. Computed: `border` is 1.32 (light)
        // / 1.42 (dark) on the page and fails outright; `borderStrong` is 3.67 / 3.93 against the
        // page ground and 3.33 / 4.16 against this field's own `surfaceSunken` fill.
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Control)
}

val LCopyFieldValueStyle = CssStyle.base {
    Modifier
        .flexGrow(1)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(1)
        .monoFont()
        .tabularNumbers()
        .padding(left = Space.MD)
        .whiteSpace(WhiteSpace.NoWrap)
        .overflow(Overflow.Hidden)
        .styleModifier {
            // No `text-overflow: ellipsis`. The elision is already computed; letting CSS add a
            // second one would silently eat the tail this component exists to preserve.
            property("min-width", "0")
        }
}

val LCopyFieldButtonStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("display", "inline-flex")
                property("flex", "0 0 auto")
                // The field is 40px tall *including* its hairline, so a child confined to the
                // padding box tops out at 38 and fails the 40px target floor. Pulling the button
                // out over the border on three sides makes the target a true 40x40 and puts its
                // radius on the field's outer corner curve, where "flush to the right edge" means
                // flush.
                property("margin", "-1px -1px -1px 0")
                property("border", "0")
            }
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .width(HIT_AREA_FLOOR_PX.px)
            .height(HIT_AREA_FLOOR_PX.px)
            .backgroundColor(Colors.Transparent)
            .color(colorMode.toSitePalette().textSecondary)
            .borderRadius(Radius.Control)
            .cursor(Cursor.Pointer)
            .interactiveTransition()
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().border)
    }
    cssRule(":active:not(:focus-visible)") {
        Modifier.pressScale().pressTransition()
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * One identifier, shown truncated, copied in full.
 *
 * The visible string is elided in the middle by [elideMiddle]; the button always writes [value] —
 * the whole of it, never what is on screen. That is the entire component. A field that copies its
 * own truncation is worse than no copy button, because the failure is invisible until somebody
 * pastes it into a support thread.
 *
 * The full value also goes into the accessibility tree beside the elided one, so a screen reader
 * hears the identifier it would be asked to quote rather than an ellipsis in the middle of it.
 *
 * **Never pass a secret to this component.** It is for a job id, an invoice number, a workspace
 * slug — an identifier that is safe to print, quote in a ticket and read aloud. A provider API key,
 * an API token, a Paddle customer id or a Paddle subscription id must **never** be passed here: not
 * in full, not truncated, not masked. Eliding is not redaction — the value is in the DOM, in the
 * accessibility tree and one click from the clipboard, and the middle of a key is not the secret
 * part of it. A credential that must be shown once has its own flow (`settings/tokens`,
 * `settings/agents`); a credential that is already stored is shown as a non-copyable hint and
 * nothing else.
 *
 * @param value the identifier, in full. This is what lands on the clipboard.
 * @param label what is being copied, as a noun — `"job ID"` gives `aria-label="Copy job ID"` and
 *   announces "Job ID copied."
 * @param modifier applied after the component's own styles, so `Modifier.width(...)` overrides the
 *   default 320. The height is fixed at 40 by the 40px target floor.
 * @param headChars how many leading characters survive the elision. Defaults to a UUID's first two
 *   hyphenated groups; widen it only alongside [modifier], since the box does not grow to fit.
 * @param tailChars how many trailing characters survive. Defaults to a UUID's last group. Never 0 —
 *   a tail-truncated identifier is the failure [elideMiddle] exists to prevent.
 * @param onCopied fired after the clipboard actually accepted the value. Deliberately not a toast:
 *   the announcement below is what accessibility needs, and where a toast belongs — or whether one
 *   belongs at all — is the calling screen's decision.
 * @param onCopyFailed fired when the clipboard refused. The reader cannot fall back to selecting
 *   the text, because the text on screen is not the value, so a screen that can offer another route
 *   to the identifier should offer it here.
 */
@Composable
fun LCopyField(
    value: String,
    label: String,
    modifier: Modifier = Modifier,
    headChars: Int = ELIDE_HEAD_CHARS,
    tailChars: Int = ELIDE_TAIL_CHARS,
    onCopied: () -> Unit = {},
    onCopyFailed: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    val confirmation = rememberCopyConfirmation()
    var announcement by remember { mutableStateOf("") }
    // Live regions announce a *change* in text. Copying the same id twice produces the same
    // sentence, which most screen readers treat as nothing having happened — so the second copy
    // would be silent, and a copy with no feedback is a copy the reader repeats. The alternating
    // trailing space changes the node's text without changing what is spoken.
    fun announce(message: String) {
        announcement = if (announcement == message) "$message " else message
    }

    Div(LCopyFieldStyle.toModifier().then(modifier).toAttrs()) {
        Span(LCopyFieldValueStyle.toModifier().toAttrs()) {
            Span(Modifier.attr("aria-hidden", "true").toAttrs()) {
                Text(elideMiddle(value, headChars, tailChars))
            }
            Span(LVisuallyHiddenStyle.toModifier().toAttrs()) { Text(value) }
        }
        Button(
            LCopyFieldButtonStyle.toModifier().toAttrs {
                type(ButtonType.Button)
                attr("aria-label", trf("ds.ds.lcopy_field.copy_labelled", "Copy {label}", "label" to label))
                onClick {
                    scope.launch {
                        runCatching { copySafely(value) }.fold(
                            {
                                announce("${label.replaceFirstChar { it.uppercase() }} copied.")
                                confirmation.confirm()
                                onCopied()
                            },
                            {
                                announce("Couldn't copy the $label.")
                                onCopyFailed()
                            },
                        )
                    }
                }
            },
        ) {
            LCopyGlyph(confirmed = confirmation.confirmed, size = IconSize.MD)
        }
        // Rendered from the first paint, empty, because a live region inserted at the same moment
        // as its text is a live region nothing announces.
        Div(
            LVisuallyHiddenStyle.toModifier().toAttrs {
                attr("role", "status")
            },
        ) {
            Text(announcement)
        }
    }
}
